import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Gauge, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MarketPressureGauge({ hours = 24 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/market-pressure?hours=${hours}`);
        if (!response.ok) throw new Error('Failed to fetch market pressure');
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [hours]);

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Gauge className="w-5 h-5" />
            Market Pressure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[150px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Market Pressure</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load data</p>
        </CardContent>
      </Card>
    );
  }

  const { pressure, buys, sells, totals } = data;
  const pressureValue = pressure.combined;
  
  // Calculate needle rotation (-90 to 90 degrees, where -90 is full bearish, 90 is full bullish)
  const needleRotation = (pressureValue / 100) * 90;
  
  const getSentimentColor = () => {
    if (pressure.sentiment === 'bullish') return 'text-emerald-500';
    if (pressure.sentiment === 'bearish') return 'text-red-500';
    return 'text-yellow-500';
  };

  const getSentimentIcon = () => {
    if (pressure.sentiment === 'bullish') return <TrendingUp className="w-5 h-5" />;
    if (pressure.sentiment === 'bearish') return <TrendingDown className="w-5 h-5" />;
    return <Minus className="w-5 h-5" />;
  };

  return (
    <Card className="h-full" data-testid="market-pressure-gauge">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Gauge className="w-5 h-5" />
          Market Pressure ({hours}h)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Gauge Visualization */}
        <div className="relative flex justify-center mb-4">
          <svg viewBox="0 0 200 120" className="w-full max-w-[200px]">
            {/* Background arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="#374151"
              strokeWidth="16"
              strokeLinecap="round"
            />
            {/* Gradient arc segments */}
            <defs>
              <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ef4444" />
                <stop offset="50%" stopColor="#eab308" />
                <stop offset="100%" stopColor="#22c55e" />
              </linearGradient>
            </defs>
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="url(#gaugeGradient)"
              strokeWidth="12"
              strokeLinecap="round"
              opacity="0.8"
            />
            {/* Needle */}
            <g transform={`rotate(${needleRotation} 100 100)`}>
              <line
                x1="100"
                y1="100"
                x2="100"
                y2="35"
                stroke="currentColor"
                strokeWidth="3"
                className="text-foreground"
              />
              <circle cx="100" cy="100" r="8" fill="currentColor" className="text-foreground" />
            </g>
            {/* Labels */}
            <text x="25" y="115" className="text-[10px] fill-red-500 font-medium">SELL</text>
            <text x="165" y="115" className="text-[10px] fill-emerald-500 font-medium">BUY</text>
          </svg>
        </div>

        {/* Sentiment Badge */}
        <div className={`flex items-center justify-center gap-2 text-lg font-bold ${getSentimentColor()} mb-4`}>
          {getSentimentIcon()}
          <span className="uppercase">{pressure.sentiment}</span>
          <span className="text-sm font-normal text-muted-foreground">
            ({pressureValue > 0 ? '+' : ''}{pressureValue.toFixed(1)})
          </span>
        </div>

        {/* Buy/Sell Stats */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="text-center p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <div className="text-emerald-500 font-bold text-lg">{buys.count.toLocaleString()}</div>
            <div className="text-muted-foreground text-xs">Buys ({buys.percentage_count}%)</div>
            <div className="text-emerald-500/70 text-xs mt-1">
              ${(buys.volume_usd / 1000).toFixed(1)}K
            </div>
          </div>
          <div className="text-center p-2 rounded-lg bg-red-500/10 border border-red-500/20">
            <div className="text-red-500 font-bold text-lg">{sells.count.toLocaleString()}</div>
            <div className="text-muted-foreground text-xs">Sells ({sells.percentage_count}%)</div>
            <div className="text-red-500/70 text-xs mt-1">
              ${(sells.volume_usd / 1000).toFixed(1)}K
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
